import { Fragment, useState } from 'react';
import clsx from 'clsx';
import { useAppDispatch } from '../../../../app/hooks';
import BottomDrawer from '../../../../common/_custom/BottomDrawer/BottomDrawer';
import { useCompanyLogo } from '../../../../common/_custom/Hooks/useCompanyLogo';
import { setShowCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../../../store/commentsThread/commentsThread_reducer';
import StatusDropdown, { StatusDropdownOption } from '../../../../common/_custom/StatusDropdown/StatusDropdown';
import { InvestorDealData } from '../../../../store/investor/reducer';
import { getInitials } from '../../../../utils/commonUtil';
import { formatDateByFormat, DATE_FORMATS } from '../../../../utils/dateUtils';
import { caretRight } from '../../../../assets/hostedassets';
import styles from './CompanyCard.module.scss';

// THE mobile company card — one component for the Leads tab, Deal View and
// Mandate View: the header/avatar/chips/comment structure is identical across
// all three; Deal View adds product badges + tap-through, Mandate View adds
// the expandable lender-wise breakdown.
// Loose shape shared by lead comments and deal comments (both optional-field)
export type CardComment = { text?: string; activityBy?: string; activityDate?: string } | null;

type MandateBreakdown = {
    investorDeals: InvestorDealData[];
    getStatusOption: (status?: string) => StatusDropdownOption | undefined;
};

type CompanyCardProps = {
    name: string;
    orgId?: string;
    comment?: CardComment;
    // Disabled dropdown-style chips after the name (lead status, owner, deal status)
    chips?: StatusDropdownOption[];
    // Plain text pills (e.g. amount)
    badges?: string[];
    // First product + "+N" counter opening the products drawer
    productTypes?: string[];
    onCardClick?: () => void;
    mandate?: MandateBreakdown;
};

const CommentBlock = ({ comment, onClick }: { comment?: CardComment; onClick: (e: React.MouseEvent) => void }) => (
    <div className={styles.CommentBox} onClick={onClick}>
        {comment?.text ? (
            <div className={styles.CommentMeta}>
                <span className={styles.CommentText}>{comment.text}</span>
                <span className={styles.CommentTimestamp}>
                    {comment.activityDate && formatDateByFormat(comment.activityDate, DATE_FORMATS.CARD_COMMENT_TIMESTAMP)}
                    {comment.activityBy && ` by ${comment.activityBy}`}
                </span>
            </div>
        ) : (
            <span className={styles.CommentPlaceholder}>Add Comment</span>
        )}
    </div>
);

const StatusChip = ({ option }: { option: StatusDropdownOption }) => (
    <div className={styles.StatusWrapper} onClick={(e) => e.stopPropagation()}>
        <StatusDropdown dropdownOptions={[]} selectedOption={option} selectOption={() => {}} disabled={true} />
    </div>
);

const CompanyCard = ({ name, orgId, comment, chips = [], badges = [], productTypes = [], onCardClick, mandate }: CompanyCardProps) => {
    const dispatch = useAppDispatch();
    const [expanded, setExpanded] = useState<boolean>(false);
    const [productsModalData, setProductsModalData] = useState<string[] | null>(null);

    // Ordered logo sources (logo → Google favicon → site favicon); advance on
    // each <img> error and fall to initials once exhausted
    const { currentSource: currentLogo, handleError: handleLogoError, handleLoad: handleLogoLoad } = useCompanyLogo({ orgId });
    const initials = getInitials(name);
    const investorDeals = mandate?.investorDeals || [];

    const openCommentsThread = (e: React.MouseEvent, threadOrgId?: string, threadName?: string) => {
        e.stopPropagation();
        dispatch(
            setShowCommentsThread({
                source: COMMENTS_THREAD_SOURCES.COMPANIES_LIST,
                assesseeOrgId: threadOrgId || orgId || '',
                headerText: threadName || name,
            }),
        );
    };

    const handleHeaderClick = () => {
        if (mandate) setExpanded((prev) => !prev);
        else onCardClick?.();
    };

    return (
        <div className={clsx(styles.Card, { [styles.Clickable]: !mandate && !!onCardClick })} onClick={mandate ? undefined : onCardClick}>
            <div className={styles.Header} onClick={mandate ? handleHeaderClick : undefined}>
                <div className={styles.Avatar}>
                    {currentLogo ? (
                        <img className={styles.AvatarImg} src={currentLogo} alt={name} onError={handleLogoError} onLoad={handleLogoLoad} />
                    ) : (
                        initials
                    )}
                </div>
                <div className={styles.HeaderInfo}>
                    <span className={styles.CompanyName}>{name}</span>
                    <div className={styles.BadgeRow}>
                        {chips.map((chip, i) => (
                            <StatusChip key={chip.value || i} option={chip} />
                        ))}
                        {badges.map((badge) => (
                            <span key={badge} className={styles.Badge}>
                                <span className={styles.BadgeLabel}>{badge}</span>
                            </span>
                        ))}
                        {productTypes.length > 0 && (
                            <span className={styles.Badge}>
                                <span className={styles.BadgeLabel}>{productTypes[0]}</span>
                                {productTypes.length > 1 && (
                                    <span
                                        className={styles.BadgeCount}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setProductsModalData(productTypes);
                                        }}
                                    >
                                        &nbsp;+{productTypes.length - 1}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                </div>
                {mandate && investorDeals.length > 0 && (
                    <div className={clsx(styles.Chevron, { [styles.ChevronExpanded]: expanded })}>
                        <img src={caretRight} alt="" className={styles.ChevronIcon} />
                    </div>
                )}
            </div>

            <div className={styles.CommentSection}>
                <CommentBlock comment={comment} onClick={(e) => openCommentsThread(e)} />
            </div>

            {/* Mandate View: lender-wise breakdown — single bordered container, dividers between lenders */}
            {mandate && expanded && investorDeals.length > 0 && (
                <div className={styles.LendersBox}>
                    {investorDeals.map((investorDeal, idx) => {
                        const lenderStatusOption = mandate.getStatusOption(investorDeal.dealStatus);
                        const dealLoanTypes = investorDeal.loanTypes || [];

                        return (
                            <Fragment key={investorDeal.dealId || idx}>
                                {idx > 0 && <div className={styles.LenderDivider} />}
                                <div className={styles.LenderBlock}>
                                    <div className={styles.LenderHeader}>
                                        <span className={styles.LenderDot} />
                                        <span className={styles.LenderName}>{investorDeal.lenderName || 'Unknown Lender'}</span>
                                    </div>
                                    <div className={styles.LenderDetails}>
                                        {lenderStatusOption && (
                                            <div className={styles.DetailItem}>
                                                <span className={styles.DetailLabel}>Status</span>
                                                <StatusChip option={lenderStatusOption} />
                                            </div>
                                        )}
                                        {investorDeal.amount && (
                                            <div className={styles.DetailItem}>
                                                <span className={styles.DetailLabel}>Amount</span>
                                                <span className={styles.DetailValue}>{investorDeal.amount}</span>
                                            </div>
                                        )}
                                        {dealLoanTypes.length > 0 && (
                                            <div className={styles.DetailItem}>
                                                <span className={styles.DetailLabel}>Product</span>
                                                <span className={styles.DetailValue}>
                                                    {dealLoanTypes[0]}
                                                    {dealLoanTypes.length > 1 && (
                                                        <span
                                                            className={styles.ProductMore}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setProductsModalData(dealLoanTypes);
                                                            }}
                                                        >
                                                            {' '}
                                                            +{dealLoanTypes.length - 1} more
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.ThreadComment}>
                                        <CommentBlock
                                            comment={investorDeal.comment}
                                            onClick={(e) => openCommentsThread(e, investorDeal.dealBeneficiaryId, investorDeal.lenderName)}
                                        />
                                    </div>
                                </div>
                            </Fragment>
                        );
                    })}
                </div>
            )}

            {productsModalData && (
                <BottomDrawer onClose={() => setProductsModalData(null)} height="auto" showHeading heading="Products">
                    <div className={styles.ProductsList}>
                        {productsModalData.map((type, i) => (
                            <div key={i} className={styles.ProductsListItem}>
                                {type}
                            </div>
                        ))}
                    </div>
                </BottomDrawer>
            )}
        </div>
    );
};

export default CompanyCard;
